/**
 * Per-provider instruction registry, backed by a vanilla zustand store.
 *
 * Each {@link createInstructionStore} instance is owned by one
 * `InstructionProvider` and never replaced, so the store reference is stable
 * for the provider's whole lifetime. Registration therefore never changes the
 * context value identity — registering one block does not re-render the others.
 * Readers subscribe with {@link selectInstructions} and re-render only when the
 * joined text they select actually changes.
 */

import { createStore } from 'zustand/vanilla';

import type { Placement, RenderedBlock } from './types.ts';

export interface InstructionState {
  /** Registered blocks, keyed by a store-minted identity. */
  blocks: Map<symbol, RenderedBlock>;
  /** Register a block; returns its unregister closure (drop-in effect cleanup). */
  register: (block: RenderedBlock) => () => void;
}

/** Stable handle to a provider-scoped instruction store. */
export type InstructionStore = ReturnType<typeof createInstructionStore>;

/**
 * Creates an instruction store. The store mints a `Symbol` identity per
 * `register` call, so callers never hold or collide on a key, and `register`
 * hands back its own unregister function.
 */
export const createInstructionStore = () =>
  createStore<InstructionState>((set) => ({
    blocks: new Map(),
    register: (block) => {
      const id = Symbol('instruction-block');
      set((state) => ({ blocks: new Map(state.blocks).set(id, block) }));
      return () =>
        set((state) => {
          const blocks = new Map(state.blocks);
          blocks.delete(id);
          return { blocks };
        });
    },
  }));

/**
 * Selector returning the registered blocks for a placement, joined in priority
 * order. The result is a primitive string, so `useStore`'s `Object.is` check
 * re-renders readers only when the joined text actually changes.
 */
export const selectInstructions =
  (placement: Placement) =>
  (state: InstructionState): string =>
    [...state.blocks.values()]
      .filter((block) => block.placement === placement)
      .toSorted((a, b) => a.priority - b.priority)
      .map((block) => block.content)
      .join('\n\n');
